import { useInfiniteQuery } from "@tanstack/react-query";
import type { AuditResponse } from "@mubin/contracts";
import { Empty, Loading, PageTitle } from "../components/ui";
import { api, errorText } from "../lib/api";
import { auditActionLabel, auditDetails } from "../lib/audit";
import { roleLabel } from "../lib/labels";
import { useOrg } from "../lib/org";

const time = new Intl.DateTimeFormat("ar-SA-u-nu-latn-ca-gregory", {
  dateStyle: "medium",
  timeStyle: "short",
});

export function AuditPage() {
  const org = useOrg();
  const log = useInfiniteQuery({
    queryKey: ["audit", org?.id],
    enabled: !!org,
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) =>
      api<AuditResponse>(
        `/v1/audit?organizationId=${org!.id}&limit=50${pageParam ? `&before=${pageParam}` : ""}`,
      ),
    getNextPageParam: (last) => last.nextBefore,
  });
  const entries = log.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <>
      <PageTitle
        title="سجل التدقيق"
        sub="كل تغيير حساس يُسجل مع صاحبه ووقته، ولا يمكن تعديل السجل أو حذفه."
      />
      <section className="card">
        {log.isPending ? (
          <Loading />
        ) : log.isError ? (
          <Empty title="تعذر التحميل">{errorText(log.error)}</Empty>
        ) : entries.length === 0 ? (
          <Empty title="لا توجد سجلات بعد" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الوقت</th>
                  <th>الإجراء</th>
                  <th>المنفّذ</th>
                  <th>التفاصيل</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="num small" style={{ whiteSpace: "nowrap" }}>
                      {time.format(new Date(e.at))}
                    </td>
                    <td>{auditActionLabel[e.action] ?? "إجراء آخر"}</td>
                    <td>
                      {e.actor?.name ?? "—"}
                      {e.actorRole && (
                        <span className="muted small">
                          {" "}
                          · {roleLabel[e.actorRole as keyof typeof roleLabel] ?? ""}
                        </span>
                      )}
                    </td>
                    <td className="small muted" style={{ maxWidth: 420 }}>
                      {auditDetails(e.after ?? e.before)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {log.hasNextPage && (
              <div className="section">
                <button
                  className="btn btn-secondary"
                  onClick={() => void log.fetchNextPage()}
                  disabled={log.isFetchingNextPage}
                >
                  {log.isFetchingNextPage ? "جارٍ التحميل…" : "تحميل المزيد"}
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
}
